import { OrganisationData } from '../contexts/OrganisationContext'
import { BillingDetailsPayloadType } from '../types/address'
import { OnboardingStep } from '../types/onboardingV2'
import { AdditionalPocContactType, OrganizationDetailsFormType } from '../types/settings'
import { capitalizeFirstCharacter, formatDate, numberWithCommas } from './helpers'
import { parseAdditionalPocContactsForBackend } from './settings'

const ONBOARDED_FROM_ORGANISATION_TYPE = 'CART'
export const PRE_ONBOARDING_STEPS = [
  OnboardingStep.user_profile,
  OnboardingStep.shopify_setup,
  OnboardingStep.theme_selection,
  OnboardingStep.collab_code,
  OnboardingStep.custom_app_integration
]
export const MANDATORY_PRE_ONBOARDING_STEPS = [
  OnboardingStep.user_profile,
  OnboardingStep.shopify_setup,
  OnboardingStep.theme_selection
]

export const SKIP_PROGRESS_ONBOARDING_STEPS = [
  OnboardingStep.user_profile,
  OnboardingStep.shopify_setup,
  OnboardingStep.theme_selection,
  OnboardingStep.collab_code,
  OnboardingStep.custom_app_integration,
  OnboardingStep.integration_setup
]

export const parseOrganisationData = (data: any): OrganisationData => {
  return {
    id: data?.id ?? '',
    name: data?.name ?? '',
    metadata: {
      shopfloPoc: data?.metadata?.shopflo_poc ?? 0,
      bannerText: data?.metadata?.banner_text ?? ''
    },
    taxDetails: {
      category: data?.tax_details?.category ?? '',
      pocDetails: getPocDetails(data?.tax_details?.poc_details) ?? [],
      billingAddress: data?.tax_details?.billing_address
        ? {
            address1: data.tax_details.billing_address.address1 ?? '',
            address2: data.tax_details.billing_address.address2 ?? '',
            city: data.tax_details.billing_address.city ?? '',
            state: data.tax_details.billing_address.state ?? '',
            country: data.tax_details.billing_address.country ?? '',
            zip: data.tax_details.billing_address.zip ?? '',
            state_code: data.tax_details.billing_address.state_code ?? ''
          }
        : null,
      shippingAddress: data?.tax_details?.shipping_address
        ? {
            address1: data.tax_details.shipping_address.address1 ?? '',
            address2: data.tax_details.shipping_address.address2 ?? '',
            city: data.tax_details.shipping_address.city ?? '',
            state: data.tax_details.shipping_address.state ?? '',
            country: data.tax_details.shipping_address.country ?? '',
            zip: data.tax_details.shipping_address.zip ?? '',
            state_code: data.tax_details.shipping_address.state_code ?? ''
          }
        : null,
      entityType: data?.tax_details?.entity_type,
      gstCertificateFileKey: data?.tax_details?.gst_certificate_file_key,
      identificationNumber: data?.tax_details?.identification_number,
      businessLegalName: data?.tax_details?.business_legal_name,
      businessTradeName: data?.tax_details?.business_trade_name,
      areAddressesSame: data?.tax_details?.are_addresses_same
    },
    billingPopUp: {
      visible: true,
      dismissible: data?.billing_popup?.dismissable ?? false,
      status: data?.billing_popup?.status ?? 'NONE',
      sentInvoiceMonth: capitalizeFirstCharacter(data?.billing_popup?.sent_invoice_month) ?? '',
      sentInvoiceDueDate: formatDate(data?.billing_popup?.sent_invoice_due_date) ?? '',
      numberOfOverDueInvoices: data?.billing_popup?.number_of_overdue_invoices ?? 0,
      overDueAmount: numberWithCommas(data?.billing_popup?.overdue_amount ?? 0, 2, true) ?? '0',
      overDueSince: data?.billing_popup?.overdue_since ?? 0,
      hasOverdueInvoices: data?.billing_popup?.has_overdue_invoices ?? false
    },
    createdAt: data?.created_at ?? '',
    isOverDueInvoicesBannerOpen: true,
    payNowClicked: false,
    superAdminEnabled: data?.configuration?.super_admin_access_enabled ?? false,
    inOnboarding: data?.in_onboarding ?? false,
    isFromShopifyPublicApp: data?.type === ONBOARDED_FROM_ORGANISATION_TYPE,
    onboardingState: {
      completed_states: data?.onboarding_state?.completed_states ?? [],
      current_state: data?.onboarding_state?.current_state ?? null,
      unlockedStates: data?.onboarding_state?.unlocked_states ?? []
    },
    shopConnectionStatus: data?.shop_connection_status,
    shopDomain: data?.shop_domain ?? '',
    status: data?.status ?? '',
    themeDetails: data?.theme_details ?? [],
    churnReason: data?.churn_reason ?? '',
    churnReviewMeetingId: data?.churn_review_meeting_id ?? '',
    churnReasonComments: data?.churn_reason_comments ?? '',
    organizationAgreement: data?.organization_agreement
      ? { status: data.organization_agreement.status ?? 'UNAPPLICABLE' }
      : null
  }
}

export const getTaxPayload = ({
  values,
  additionalPocContacts,
  isShippingSame
}: {
  values: OrganizationDetailsFormType
  additionalPocContacts: AdditionalPocContactType[]
  isShippingSame: boolean
}) => {
  const primaryPocContact = {
    name: values?.poc_name,
    email: values?.poc_email_id,
    phone: values?.poc_phone_number,
    designation: 'Primary Finance POC',
    primary: true
  }
  let payload: BillingDetailsPayloadType = {
    tax_type: '',
    identification_number: values?.identification_number,
    directors_name: '',
    directors_email_id: '',
    category: values?.category,
    entity_name: values?.gst_business_name,
    entity_type: values?.tax_type,
    business_trade_name: values?.business_trade_name,
    business_legal_name: values?.business_legal_name,
    are_addresses_same: isShippingSame,
    billing_address: {
      address1: values?.billing_address1,
      address2: values?.billing_address2,
      zip: values?.billing_zip,
      city: values?.billing_city,
      state: values?.billing_state,
      state_code: values?.billing_state_code,
      country: values?.billing_country ?? 'India',
      country_code: values?.billing_country_code ?? 'IN'
    },
    shipping_address: {
      address1: values?.shipping_address1,
      address2: values?.shipping_address2,
      zip: values?.shipping_zip,
      city: values?.shipping_city,
      state: values?.shipping_state,
      state_code: values?.shipping_state_code,
      country: values?.shipping_country ?? 'India',
      country_code: values?.shipping_country_code ?? 'IN'
    },
    poc_details: parseAdditionalPocContactsForBackend(additionalPocContacts, primaryPocContact)
  }

  if (values.gstFileUrl) {
    payload = {
      ...payload,
      gst_certificate_file_key: values.gstFileUrl ?? ''
    }
  }

  return payload
}

export const getPocDetails = (pocDetails: any) => {
  if (!Boolean(pocDetails) || pocDetails.length === 0) return []
  return pocDetails.map((poc: any) => ({
    pocName: poc.name,
    pocEmail: poc.email,
    pocPhone: poc.phone,
    isPrimaryPoc: poc.primary,
    designation: poc.designation
  }))
}

export const getDueStatusText = (
  dueStatus: string,
  dueDate: string,
  isDueDatePassed: boolean,
  header: boolean = false
) => {
  if (dueStatus === 'today') return header ? 'due today' : 'is due today'
  else if (dueStatus === 'tomorrow') return header ? 'due tomorrow' : 'is due tomorrow'
  else if (isDueDatePassed) return header ? `` : `has been due since ${dueDate}`
  else return header ? `is due in ${getNumberOfDaysCount(dueDate)} days` : `due on ${dueDate}`
}

export const getNumberOfDaysCount = (dueDateStr: string) => {
  const today = new Date()
  const dueDate = new Date(dueDateStr)
  return Math.round((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

interface DueDateStatus {
  isDueDatePassed: boolean
  dueStatus: string
}

export const getDueDateStatus = (sentInvoiceDueDate: string): DueDateStatus => {
  const dueDate = new Date(sentInvoiceDueDate)
  const today = new Date()

  const isSameDate =
    dueDate.getFullYear() === today.getFullYear() &&
    dueDate.getMonth() === today.getMonth() &&
    dueDate.getDate() === today.getDate()

  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow =
    dueDate.getFullYear() === tomorrow.getFullYear() &&
    dueDate.getMonth() === tomorrow.getMonth() &&
    dueDate.getDate() === tomorrow.getDate()

  let status = ''
  if (isSameDate) status = 'today'
  else if (isTomorrow) status = 'tomorrow'

  return {
    isDueDatePassed: dueDate.getTime() < today.getTime(),
    dueStatus: status
  }
}

export const transformTaxDetailsToLocationDetails = (taxDetails: any) => {
  return {
    billing: taxDetails?.billingAddress
      ? {
          address1: taxDetails.billingAddress.address1,
          address2: taxDetails.billingAddress.address2,
          district: taxDetails.billingAddress.city,
          state: taxDetails.billingAddress.state,
          state_code: taxDetails.billingAddress.state_code,
          country: taxDetails.billingAddress.country,
          zip: taxDetails.billingAddress.zip
        }
      : null,
    shipping: taxDetails?.shippingAddress
      ? {
          address1: taxDetails.shippingAddress.address1,
          address2: taxDetails.shippingAddress.address2,
          district: taxDetails.shippingAddress.city,
          state: taxDetails.shippingAddress.state,
          state_code: taxDetails.shippingAddress.state_code,
          country: taxDetails.shippingAddress.country,
          zip: taxDetails.shippingAddress.zip
        }
      : null
  }
}

export const getInitialShippingSameAsBilling = (taxDetails: any): boolean => {
  return taxDetails?.areAddressesSame ?? true
}

export const inPreOnboarding = (organisationData: OrganisationData): boolean => {
  const completedSteps = organisationData?.onboardingState?.completed_states ?? []
  return !MANDATORY_PRE_ONBOARDING_STEPS.every(step => completedSteps.includes(step))
}

export const checkShopConnectionStatus = (status: string, onboardingState: OrganisationData['onboardingState']) => {
  return (
    status === 'LIVE' ||
    status === 'DISABLED' ||
    status === 'CHURN_REVIEW' ||
    !onboardingState?.current_state ||
    onboardingState.completed_states?.includes(OnboardingStep.go_live)
  )
}
